import { KeyboardEvent, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2, Lock, Search, X } from "lucide-react";
import { Kbd } from "~/components/Kbd";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import {
  ENTITY_TYPE_PLURAL,
  entityHref,
  errorMessage,
} from "~/pages/manifold-nextgen/components/search-manager/constants";
import {
  SEARCH_ENTITY_TYPES,
  SEARCH_MAX_RESULTS,
  SearchEntityType,
  SearchResponse,
  searchPath,
  searchTotal,
} from "~/services/search";
import { QueryText } from "./QueryText";
import { ScopeFilter, typeList } from "./ScopeFilter";
import { RESULT_ATTRIBUTE, SearchResultRow } from "./SearchResultRow";
import { useHitDetails } from "./useHitDetails";
import { useSlashToFocus } from "./useSlashToFocus";

const PAGE_SIZE = 20;

const PARAM = { QUERY: "q", TYPE: "type", UPDATED: "updated" } as const;

// `Select` items need non-empty values.
const ANY_TIME = "any";

// Filters on each entity's own last update, not on when it was indexed.
const RECENCY_OPTIONS = [
  { value: ANY_TIME, label: "Updated any time" },
  { value: "7", label: "Updated in the last 7 days" },
  { value: "30", label: "Updated in the last 30 days" },
  { value: "90", label: "Updated in the last 90 days" },
  { value: "365", label: "Updated in the last year" },
];

const SKELETON_ROWS = 5;

const ALL_TYPES_PHRASE = typeList(SEARCH_ENTITY_TYPES);

interface SearchExperienceProps {
  /** Offer a switch that shows how each hit was ranked. */
  diagnostics?: boolean;
}

/**
 * Search across every entity type the caller can see, narrowable to one type
 * and a recency window. The query, scope and window live in the URL, so a
 * search can be reloaded, shared and stepped back through.
 */
export const SearchExperience = ({ diagnostics = false }: SearchExperienceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Typed text stays local, since the URL updates asynchronously and would move the caret.
  const [query, setQuery] = useState(() => searchParams.get(PARAM.QUERY) ?? "");
  const scope = parseScope(searchParams.get(PARAM.TYPE));
  const updatedWithinDays = parseRecency(searchParams.get(PARAM.UPDATED));
  const debouncedQuery = useDebounce(query).trim();

  // "Show more" holds only for the search it was asked on.
  const searchKey = [debouncedQuery, scope, updatedWithinDays].join("|");
  const [expandedSearch, setExpandedSearch] = useState<string | null>(null);
  const limit = expandedSearch === searchKey ? SEARCH_MAX_RESULTS : PAGE_SIZE;
  const [showRanking, setShowRanking] = useState(false);

  const { data, error, isLoading, isValidating, mutate } = useFetcher<SearchResponse>(
    searchPath(debouncedQuery, scope ? [scope] : SEARCH_ENTITY_TYPES, limit, updatedWithinDays),
    // Keep the last results on screen while the next keystroke's load.
    { keepPreviousData: true }
  );

  const hitDetails = useHitDetails();

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();

  useSlashToFocus(inputRef);

  const updateParams = (
    changes: Partial<Record<(typeof PARAM)[keyof typeof PARAM], string | null>>
  ) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) =>
          value ? next.set(key, value) : next.delete(key)
        );
        return next;
      },
      { replace: true }
    );

  const changeQuery = (next: string) => {
    setQuery(next);
    updateParams({ [PARAM.QUERY]: next || null });
  };

  const resultLinks = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLAnchorElement>(`[${RESULT_ATTRIBUTE}]`) ?? []);

  const hasQuery = query.trim().length > 0 && debouncedQuery.length > 0;
  const hits = hasQuery ? (data?.hits ?? []) : [];
  const hitsMatchQuery = !isLoading && query.trim() === debouncedQuery;
  const inaccessible = new Set(data?.inaccessible_types ?? []);
  const total = data ? Math.max(searchTotal(data), hits.length) : hits.length;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      resultLinks()[0]?.focus();
    } else if (
      event.key === "Enter" &&
      !event.nativeEvent.isComposing &&
      hitsMatchQuery &&
      hits.length > 0
    ) {
      event.preventDefault();
      navigate(entityHref(hits[0].entity_type, hits[0].entity_id));
    } else if (event.key === "Escape" && query) {
      event.preventDefault();
      changeQuery("");
    }
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const links = resultLinks();
    const index = links.findIndex((link) => link === document.activeElement);
    if (index === -1) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      links[Math.min(index + 1, links.length - 1)].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index === 0) {
        inputRef.current?.focus();
      } else {
        links[index - 1].focus();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      inputRef.current?.focus();
    }
  };

  const scopePhrase = scope ? ENTITY_TYPE_PLURAL[scope].toLowerCase() : ALL_TYPES_PHRASE;

  return (
    <div className="flex flex-col gap-4">
      <div role="search" className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          type="search"
          aria-label="Search"
          aria-keyshortcuts="/"
          value={query}
          onChange={(event) => changeQuery(event.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder={`Search ${scopePhrase}`}
          autoComplete="off"
          spellCheck={false}
          className="h-12 rounded-xl pl-12 pr-20 text-base shadow-sm md:text-base [&::-webkit-search-cancel-button]:hidden"
        />
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
          {hasQuery && isValidating && (
            <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
          )}
          {query ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Clear search"
              onClick={() => {
                changeQuery("");
                inputRef.current?.focus();
              }}
            >
              <X className="size-4" />
            </Button>
          ) : (
            <Kbd className="border-border bg-muted text-muted-foreground">/</Kbd>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <ScopeFilter
          value={scope}
          onChange={(next) => updateParams({ [PARAM.TYPE]: next })}
          inaccessible={inaccessible}
        />

        <div className="flex items-center gap-4">
          {diagnostics && (
            <div className="flex items-center gap-2">
              <Switch
                id="search-ranking-details"
                checked={showRanking}
                onCheckedChange={setShowRanking}
              />
              <Label htmlFor="search-ranking-details" className="text-sm text-muted-foreground">
                Ranking details
              </Label>
            </div>
          )}
          <Select
            value={updatedWithinDays === null ? ANY_TIME : String(updatedWithinDays)}
            onValueChange={(next) =>
              updateParams({ [PARAM.UPDATED]: next === ANY_TIME ? null : next })
            }
          >
            <SelectTrigger size="sm" aria-label="Last updated">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {RECENCY_OPTIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!hasQuery && <IdleState />}

      {hasQuery && error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Search failed</AlertTitle>
          <AlertDescription>
            {/* The server's message: a search also asks the services that decide visibility. */}
            <p>{errorMessage(error)}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => mutate()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {hasQuery && !error && isLoading && hits.length === 0 && <ResultsSkeleton />}

      {hasQuery && !error && !isLoading && data && hits.length === 0 && (
        <EmptyResults
          query={debouncedQuery}
          scope={scope}
          isInaccessible={
            scope ? inaccessible.has(scope) : inaccessible.size === SEARCH_ENTITY_TYPES.length
          }
          isRecencyLimited={updatedWithinDays !== null}
          onSearchEverything={() => updateParams({ [PARAM.TYPE]: null })}
          onSearchAnyTime={() => updateParams({ [PARAM.UPDATED]: null })}
        />
      )}

      {hasQuery && !error && data && hits.length > 0 && (
        <section
          aria-label="Search results"
          aria-busy={isValidating}
          className={cn("flex flex-col gap-2 transition-opacity", isValidating && "opacity-60")}
        >
          <p className="px-3 text-xs text-muted-foreground" aria-live="polite">
            {total > hits.length && `${hits.length} of `}
            {total} {total === 1 ? "result" : "results"}
            {scope && ` in ${ENTITY_TYPE_PLURAL[scope].toLowerCase()}`}
            {showRanking && (
              <>
                {" "}
                · vector ranking {data.vector_candidates} · lexical ranking{" "}
                {data.lexical_candidates} · {data.took_ms} ms
                {data.lexical_candidates === 0 && (
                  <span className="ml-2 text-amber-600 dark:text-amber-500">
                    lexical ranking empty, so the order is the vector ranking alone
                  </span>
                )}
              </>
            )}
          </p>
          <ul ref={listRef} onKeyDown={handleListKeyDown} className="-mx-3 flex flex-col sm:mx-0">
            {hits.map((hit) => (
              <SearchResultRow
                key={`${hit.entity_type}:${hit.entity_id}`}
                hit={hit}
                query={debouncedQuery}
                showRanking={showRanking}
                details={hitDetails(hit)}
              />
            ))}
          </ul>
          <ResultsFooter
            canShowMore={hits.length === limit && limit < SEARCH_MAX_RESULTS}
            isAtCap={hits.length === SEARCH_MAX_RESULTS}
            onShowMore={() => setExpandedSearch(searchKey)}
          />
        </section>
      )}
    </div>
  );
};

const IdleState = () => (
  <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
    <span className="mb-2 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Search className="size-5" aria-hidden="true" />
    </span>
    <p className="text-sm font-medium text-foreground">Search your {ALL_TYPES_PHRASE}</p>
  </div>
);

// Mirrors a result row: icon tile beside a title line and a snippet line.
const ResultsSkeleton = () => (
  <div role="status" aria-label="Searching">
    <div aria-hidden="true" className="flex flex-col">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <div key={index} className="flex items-start gap-3 px-3 py-3">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex flex-1 flex-col gap-2 pt-0.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-5/6" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface EmptyResultsProps {
  query: string;
  scope: SearchEntityType | null;
  isInaccessible: boolean;
  isRecencyLimited: boolean;
  onSearchEverything: () => void;
  onSearchAnyTime: () => void;
}

/** Says whether rephrasing could help: no access and no match both look empty. */
const EmptyResults = ({
  query,
  scope,
  isInaccessible,
  isRecencyLimited,
  onSearchEverything,
  onSearchAnyTime,
}: EmptyResultsProps) => {
  if (isInaccessible) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <Lock className="size-5 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          You don&apos;t have access to any{" "}
          {scope ? ENTITY_TYPE_PLURAL[scope].toLowerCase() : ALL_TYPES_PHRASE}.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <p className="text-sm text-foreground">
        No results for <QueryText>{query}</QueryText>
        {scope && ` in ${ENTITY_TYPE_PLURAL[scope].toLowerCase()}`}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Try different words. New and changed items take about 15 minutes to become searchable.
      </p>
      {(scope || isRecencyLimited) && (
        <div className="mt-2 flex gap-2">
          {scope && (
            <Button variant="outline" size="sm" onClick={onSearchEverything}>
              Search everything
            </Button>
          )}
          {isRecencyLimited && (
            <Button variant="outline" size="sm" onClick={onSearchAnyTime}>
              Remove the date filter
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

interface ResultsFooterProps {
  canShowMore: boolean;
  isAtCap: boolean;
  onShowMore: () => void;
}

const ResultsFooter = ({ canShowMore, isAtCap, onShowMore }: ResultsFooterProps) => (
  <div className="flex flex-col items-center gap-3 pt-2 empty:hidden">
    {canShowMore && (
      <Button variant="outline" size="sm" onClick={onShowMore}>
        Show more results
      </Button>
    )}
    {isAtCap && (
      <p className="text-xs text-muted-foreground">
        These are the top {SEARCH_MAX_RESULTS}. Add words to your search to narrow it.
      </p>
    )}
  </div>
);

function parseScope(value: string | null): SearchEntityType | null {
  return SEARCH_ENTITY_TYPES.find((entityType) => entityType === value) ?? null;
}

/** Only the offered windows, so the dropdown always shows the filter in force. */
function parseRecency(value: string | null): number | null {
  return value !== ANY_TIME && RECENCY_OPTIONS.some((option) => option.value === value)
    ? Number(value)
    : null;
}
